import { Component, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { IPlacemark } from '../../../../../Store/placemarks.state/placemarks.model';
import { CMD_ACTIONS, CMD_TARGETS, CmdRouterService } from '../../../../../services/cmd-router.service';
import { MatIcon } from '@angular/material/icon';
import { PerfectScrollbarModule } from 'perfect-scrollbar-angular';
import { TooltipDirective } from '../../../../../Directives/tooltip.directive';
import { FormsModule } from '@angular/forms';
import { SafePipe } from 'src/app/Pipes/safe.pipe';
import { ViewerObjectType } from 'src/app/common/Models/UI/viewer-object-type.enum';
import { CommonUtilitySvc } from 'src/app/services/common-utility.service';
import { Store } from '@ngxs/store';
import { PlacemarksState } from '../../../../../Store/placemarks.state/placemarks.state';
import { BaseTourState } from '../../../../../Store/baseTour.state/baseTour.state';
import { IBaseTour } from '../../../../../Store/baseTour.state/baseTour.model';
import { IViewpoint } from 'src/app/Store/viewpoints.state/viewpoints.model';

const NO_PLACEMARKS_AVAILABLE: string = 'No placemarks are available for this step.';
const NO_RESULTS_FOUND: string = 'No results found.';

@Component({
  selector: 'ins-tour-placemark-menu',
  templateUrl: './tour-placemark-menu.component.html',
  styleUrls: ['./tour-placemark-menu.component.scss', './../submenus-shared-design.scss',
    './../../../../../common/UI-Components/shared-UI-components.scss'],
  imports: [MatIcon, FormsModule, PerfectScrollbarModule, TooltipDirective, SafePipe]
})
export class TourPlacemarkMenuComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private subscriptionsInitialized = false;
  public filterStr: string = '';
  
  @Output() exitPanelEvent: EventEmitter<void> = new EventEmitter<void>();

  public placemarks: IPlacemark[] = [];
  public hover: { [key: string]: boolean } = {};
  public placemarkExpandedState: { [key: string]: boolean } = {};

  // Store observables for placemark state management
  public placemarks$: Observable<IPlacemark[]>;
  public removedPlacemarkId$: Observable<string>;
  public tour$: Observable<IBaseTour>;
  public currentStepId$: Observable<number | null>;
  public existingPanoStepPlacemarkIds: string[] = [];
  public tour: IBaseTour;
  public isPanoramicStep: boolean = false;

  @Output()
  public closePanel: EventEmitter<void> = new EventEmitter();

  get noContentAvailableStr(): string {
    if (this.filterStr.trim() === '') {
      return NO_PLACEMARKS_AVAILABLE;
    } else {
      return NO_RESULTS_FOUND;
    }
  }

  constructor(
    private readonly cmdRouterSvc: CmdRouterService, 
    private readonly commonUtilitySvc: CommonUtilitySvc,
    private readonly store: Store
  ) {    
    // Initialize store observables for different placemark operations
    this.placemarks$ = this.store.select(PlacemarksState.getPlacemarks);
    this.removedPlacemarkId$ = this.store.select(PlacemarksState.getRemovedPlacemarkId);
    this.tour$ = this.store.select(BaseTourState.getBaseTour);
    this.currentStepId$ = this.store.select(BaseTourState.getCurrentStepID);
  }

  ngOnInit(): void {
    // Subscribe to currentStepId changes
    this.currentStepId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentStepId: number | null) => {
        this.calculatePanoramicStep(currentStepId);
      });

    // First subscribe to tour, then initialize other subscriptions only when tour is available
    this.tour$
      .pipe(takeUntil(this.destroy$))
      .subscribe((tour: IBaseTour) => {
        if (tour) {
          this.tour = tour;
          
          // Only subscribe to other observables once, when tour is first available
          if (this.subscriptionsInitialized) {
            // Tour changed, re-filter existing placemarks with new visibility rules
            this.placemarks = this.placemarks.filter(pm => 
              this.isVisible(pm.parentLayerId, pm.parentZoneId)
            );
          } else {
            this.subscriptionsInitialized = true;
            
            // Now that tour is available, subscribe to placemarks
            this.placemarks$
              .pipe(takeUntil(this.destroy$))
              .subscribe((placemarks: IPlacemark[]) => {
                // Clear previous list
                this.placemarks = [];
                
                if (placemarks.length > 0) {
                  // Add all visible placemarks to the list
                  this.placemarks = placemarks.filter(pm => {
                    const isVisible = this.isVisible(pm.parentLayerId, pm.parentZoneId);
                    
                    if (this.isPanoramicStep) {
                      // For panoramic steps, check both visibility and if placemark is in current step as new placemark should not be added to the current step directly
                      return isVisible && this.existingPanoStepPlacemarkIds.includes(pm.id);
                    } else {
                      // For non-panoramic steps, only check visibility
                      return isVisible;
                    }
                  });
                }
              });

            // Subscribe to removed placemark ID
            this.removedPlacemarkId$
              .pipe(takeUntil(this.destroy$))
              .subscribe((removedId: string) => {
                if (removedId) {
                  this.placemarks = this.placemarks.filter(pm => pm.id !== removedId);
                }
              });
          }
        }
      });
  }

  isVisible(layerId: string | number, zoneId: string | number): boolean {   
    // Check if layer is visible first (early return for better performance)
    if (!this.tour.visibleLayers.includes(String(layerId))) {
      return false;
    }
    
    // Check if zone is visible
    return this.tour.visibleZones.includes(String(zoneId));
  }

  private calculatePanoramicStep(currentStepId: number): void {
    if (this.tour && currentStepId !== null) {
      const currentStep = this.tour.steps[currentStepId - 1]; // Step IDs are 1-based, array is 0-based
      this.isPanoramicStep = false;
      const vp: IViewpoint = currentStep.view_point;
      if (vp != null && vp != undefined) {
        if (vp.parent === 'PANORAMIC') {
          this.isPanoramicStep = true;
          this.existingPanoStepPlacemarkIds = currentStep.placemarks;
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public clearFilterStr(): void {
    this.filterStr = '';
  }

  public getPlacemarks(): IPlacemark[] {
    return this.placemarks.filter((placemark: IPlacemark) =>
      placemark.name.toLowerCase().includes(this.filterStr.toLowerCase())
    );
  }

  getToolTipConf(element: any, label: string): any {
    let toolTipConf = {text: '', position: { H: 'hLeft', V:'vBottom' }, type: 'info'};
    if ( element.offsetWidth < element.scrollWidth )  {
      toolTipConf.text = label;
    }
    return toolTipConf;
  }

  displayResultOnGraphic(placemark: IPlacemark): void {
    this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.TOUR_MANAGER, CMD_ACTIONS.ZOOM_IN_TO_OBJECT, {data: {objectType: ViewerObjectType.PLACEMARK, id: placemark.id}});
  }

  openPlacemark(placemark: IPlacemark): void {
    this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.TOUR_MANAGER, CMD_ACTIONS.OPEN_PLAEMARK, {placemark});
  }

  trackByPlacemarkId(index: number, placemark: IPlacemark): string {
    return placemark.id;
  }

  getPlacemarkIcon(placemark: IPlacemark): string {
    return this.commonUtilitySvc.setIconForPM(placemark.parentLayerId, placemark);
  }
}
